import { PublicKey } from '@solana/web3.js';

/**
 * cp-swap (Raydium CPMM fork) PDA derivations. These mirror the byte-literal
 * seeds in `contracts/morfi-cp-swap/programs/cp-swap/src/lib.rs` + the state
 * modules. They are SEPARATE from the markets-program `b"amm_pool"` PDA
 * (`deriveAmmPoolPda` in `./derivations.ts`) — that seed belongs to the old
 * markets layout and is unrelated to the graduated cp-swap pool below.
 *
 * Used by `buildGraduate` (which CPIs into cp-swap) and by `buildAmmSwap`
 * (which calls cp-swap's `swap_base_input` directly for graduated trading).
 */

/** Program-level authority PDA seed: `b"vault_and_lp_mint_auth_seed"`. */
const CP_AUTHORITY_SEED: Buffer = Buffer.from('vault_and_lp_mint_auth_seed');
/** Canonical Morfi AmmConfig PDA seed: `[b"amm_config", 0u16.to_be_bytes()]`. */
const CP_AMM_CONFIG_SEEDS: readonly [Buffer, Buffer] = [
  Buffer.from('amm_config'),
  Buffer.from([0, 0]),
];
/** Pool state PDA seed: `b"pool"`. */
const CP_POOL_SEED: Buffer = Buffer.from('pool');
/** LP mint PDA seed: `b"pool_lp_mint"`. */
const CP_LP_MINT_SEED: Buffer = Buffer.from('pool_lp_mint');
/** Pool token vault PDA seed: `b"pool_vault"`. */
const CP_POOL_VAULT_SEED: Buffer = Buffer.from('pool_vault');
/** Observation (oracle) state PDA seed: `b"observation"`. */
const CP_OBSERVATION_SEED: Buffer = Buffer.from('observation');
/** Morfi LP-lock PDA seed: `b"morfi_lp_lock"`. */
const CP_LP_LOCK_SEED: Buffer = Buffer.from('morfi_lp_lock');

function derivePda(seeds: Buffer[], programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}

/**
 * Order a mint pair lexicographically by raw bytes. cp-swap stores the smaller
 * key as `token_0_mint` and the larger as `token_1_mint`; every pool/vault
 * derivation depends on this ordering.
 */
export function sortMintPair(a: PublicKey, b: PublicKey): [PublicKey, PublicKey] {
  return Buffer.compare(a.toBuffer(), b.toBuffer()) < 0 ? [a, b] : [b, a];
}

/** Program authority PDA. Seeds: `[b"vault_and_lp_mint_auth_seed"]`. */
export function deriveAmmAuthorityPda(ammProgramId: PublicKey): PublicKey {
  return deriveAmmAuthorityPdaWithBump(ammProgramId)[0];
}

/** Program authority PDA and canonical bump. */
export function deriveAmmAuthorityPdaWithBump(ammProgramId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([CP_AUTHORITY_SEED], ammProgramId);
}

/** Canonical Morfi AmmConfig index-0 PDA (the index seed is u16 big-endian). */
export function deriveAmmConfigPda(ammProgramId: PublicKey): PublicKey {
  return derivePda([...CP_AMM_CONFIG_SEEDS], ammProgramId);
}

/**
 * Pool state PDA. Seeds: `[b"pool", amm_config, token_0_mint, token_1_mint]`.
 * Pass `mint0`/`mint1` already ordered via {@link sortMintPair}.
 */
export function deriveAmmPoolStatePda(
  ammConfig: PublicKey,
  mint0: PublicKey,
  mint1: PublicKey,
  ammProgramId: PublicKey,
): PublicKey {
  return derivePda(
    [CP_POOL_SEED, ammConfig.toBuffer(), mint0.toBuffer(), mint1.toBuffer()],
    ammProgramId,
  );
}

/** LP mint PDA. Seeds: `[b"pool_lp_mint", pool_state]`. */
export function deriveAmmLpMintPda(poolState: PublicKey, ammProgramId: PublicKey): PublicKey {
  return derivePda([CP_LP_MINT_SEED, poolState.toBuffer()], ammProgramId);
}

/** Pool token vault PDA. Seeds: `[b"pool_vault", pool_state, mint]`. */
export function deriveAmmPoolVaultPda(
  poolState: PublicKey,
  mint: PublicKey,
  ammProgramId: PublicKey,
): PublicKey {
  return derivePda([CP_POOL_VAULT_SEED, poolState.toBuffer(), mint.toBuffer()], ammProgramId);
}

/** Observation state PDA. Seeds: `[b"observation", pool_state]`. */
export function deriveAmmObservationPda(poolState: PublicKey, ammProgramId: PublicKey): PublicKey {
  return derivePda([CP_OBSERVATION_SEED, poolState.toBuffer()], ammProgramId);
}

/** Morfi LP-lock PDA (receives the permanently-locked LP supply). Seeds: `[b"morfi_lp_lock", pool_state]`. */
export function deriveAmmLpLockPda(poolState: PublicKey, ammProgramId: PublicKey): PublicKey {
  return derivePda([CP_LP_LOCK_SEED, poolState.toBuffer()], ammProgramId);
}
